import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  FooterBox,
  StyledPaper,
  StyledTableCellHead,
  StyledTableHead,
  ellipsisSx,
} from "./style";
import OrderStatusActions from "./OrderStatusActions";
import OrderTrackingEditableFields from "./OrderTrackingEditableFields";
import { resolveTrackingHref } from "../../../utils/orderListTable";

const TRACKING_EDIT_COLUMNS = ["TRACKING", "CARRIER", "TRACKING URL"];

export default function OrderTable({
  columns = [],
  rows = [],
  selectedTab = 0,
  tabLabels = [],
  recordList,
  /** e.g. "/dashboard/orders" — order # link opens order details */
  orderDetailBasePath,
  editableTracking = false,
}) {
  const navigate = useNavigate();

  const goToOrderDetail = (row) => {
    if (orderDetailBasePath && row?.id) {
      navigate(`${orderDetailBasePath}/${row.id}`);
    }
  };

  const renderCell = (col, row) => {
    switch (col) {
      case "ORDER":
        return orderDetailBasePath && row?.id ? (
          <Link
            component="button"
            variant="body2"
            underline="hover"
            color="primary"
            fontWeight={500}
            onClick={(e) => {
              e.stopPropagation();
              goToOrderDetail(row);
            }}
            sx={{ textAlign: "left", p: 0 }}
          >
            {row.order_number || "-"}
          </Link>
        ) : (
          <Typography color="primary" fontWeight={500}>
            {row.order_number || "-"}
          </Typography>
        );
      case "TOTAL ITEMS":
        return row.approved_items_count ?? 0;
      case "STATUS":
        return row.status ?? "-";
      case "TRACKING":
      case "CARRIER":
      case "TRACKING URL":
        if (editableTracking && col === "TRACKING") {
          return <OrderTrackingEditableFields row={row} recordList={recordList} />;
        }
        if (editableTracking) {
          return null;
        }
        if (col === "TRACKING") {
          return (
            <Typography component="span" sx={ellipsisSx}>
              {row.tracking_number ?? "-"}
            </Typography>
          );
        }
        if (col === "CARRIER") {
          return (
            <Typography component="span" sx={ellipsisSx}>
              {row.carrier ?? "-"}
            </Typography>
          );
        }
        {
          const trackingHref = resolveTrackingHref(row.tracking_url);
          if (trackingHref) {
            return (
              <Link
                href={trackingHref}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                color="primary"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  ...ellipsisSx,
                  display: "inline-block",
                  maxWidth: 220,
                  fontWeight: 500,
                }}
              >
                {row.tracking_url}
              </Link>
            );
          }
          return (
            <Typography component="span" sx={ellipsisSx}>
              -
            </Typography>
          );
        }
      case "ACTION":
        return (
          <OrderStatusActions row={row} recordList={recordList} />
        );
      default:
        return row[col] ?? "-";
    }
  };

  return (
    <StyledPaper tabLabel={tabLabels[selectedTab]}>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: editableTracking ? 1150 : 700 }}>
          <StyledTableHead tabLabel={tabLabels[selectedTab]}>
            <TableRow>
              {columns.map((col, i) => (
                <StyledTableCellHead
                  key={`${col}-${i}`}
                  sx={{
                    whiteSpace: "nowrap",
                    minWidth:
                      col === "TRACKING URL"
                        ? 240
                        : col === "TRACKING" || col === "CARRIER"
                          ? 200
                          : 100,
                  }}
                >
                  {col}
                </StyledTableCellHead>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography>No records found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <TableRow key={row.id || idx} hover>
                  {columns.map((col) => {
                    if (editableTracking && (col === "CARRIER" || col === "TRACKING URL")) {
                      return null;
                    }

                    return (
                      <TableCell
                        key={`${row.id || idx}-${col}`}
                        colSpan={editableTracking && col === "TRACKING" ? 3 : undefined}
                        sx={{
                          whiteSpace: TRACKING_EDIT_COLUMNS.includes(col)
                            ? undefined
                            : "nowrap",
                          verticalAlign: editableTracking && col === "TRACKING" ? "top" : undefined,
                          py: editableTracking && col === "TRACKING" ? 1.5 : undefined,
                        }}
                        onClick={
                          col === "ACTION"
                            ? (e) => e.stopPropagation()
                            : undefined
                        }
                      >
                        {renderCell(col, row)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <FooterBox>
        <Typography variant="body2">Showing {rows.length} results</Typography>
      </FooterBox>
    </StyledPaper>
  );
}
